"use client";

import { useCallback, useContext } from "react";
import { RootStoreContext } from "../context";
import type { UserOnboardingModel } from "../../mobx/core/user/model.user";

/**
 * Client-only onboarding accessor. Returns `undefined` during SSR since the
 * onboarding state is populated lazily on the client via
 * `useFetchOnboarding()`.
 */
export function useOnboarding(): UserOnboardingModel | undefined {
  const root = useContext(RootStoreContext);
  if (!root) return;
  return root.onboarding;
}

/**
 * Client-only setter for the onboarding model. Hydrates the root store's
 * onboarding prop with a fresh `UserOnboardingModel`. No-op during SSR.
 *
 * Memoized so consumers can put the returned fn in a useEffect dep array
 * without causing re-runs on every render.
 */
export function useSetOnboarding(): (data: {
  complete: boolean;
  step: string;
}) => void {
  const root = useContext(RootStoreContext);
  return useCallback(
    (data: { complete: boolean; step: string }) => {
      root?.setOnboarding(data);
    },
    [root],
  );
}
